import React, { FunctionComponent, useEffect, useState } from 'react';
import { compose } from 'recompose';
import withStyles from 'react-jss';
import { Grid, Container } from 'semantic-ui-react';
import { withRouter, match } from 'react-router-dom';
import { pathOr, isEmpty } from 'ramda';

import Query from 'components/Query';
import PlacesMap from 'components/PlacesMap';
import { ChartPlaceholder } from 'components/Placeholder';
import { AccountDetails, useGetMerchantInfo } from 'helpers/useGetMerchantInfo';
import { usePlacesSearch } from 'helpers/usePlacesSearch';
import { ClassProp } from 'helpers/types';

import RenderMerchantOnboardVenues from './sections/MerchantOnboardVenues';
import PlacesFilters from './components/PlacesFilters';
import { usePlacesFilters } from './components/PlacesFilters/usePlacesFilter';

import { GET_PLACES } from './MerchantVenuesOnboarding.queries';
import {
  Place,
  PlacesServiceParams,
  GeoViewPort,
  applyFiltersToPlaces,
  sortPlacesByState,
  DEFAULT_GEO_CODE,
  DEFAULT_GEO_VIEWPORT,
  VENUE_LIMIT,
  MIN_NUMBER_OF_PLACES_TO_SHOW_FILTERS,
} from './MerchantVenuesOnboarding.helpers';
import styles from './MerchantVenuesOnboarding.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type QueryResponse = {
  loading: boolean;
  data: {
    getPlaces: {
      places: Place[];
      nextPage: boolean;
    };
  };
};

type MerchantInfo = {
  merchantAcctDetails: AccountDetails;
  loading: boolean;
};

type Props = {
  classes: Styles;
  match: match;
};

const renderVenuesToOnboard = (
  classes: Styles,
  filteredPlaces: Place[] | [],
  stateFilter: string,
  searchLoading: boolean,
  showFilters: boolean,
  viewport: GeoViewPort,
  handleAddMissingVenue: (newPlace: Place) => void,
  handleGetMorePlaces: Function,
) => (response: QueryResponse) => {
  if (response.loading || searchLoading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  return (
    <Container className={classes.container}>
      <PlacesMap
        places={filteredPlaces as Place[]}
        stateFilter={stateFilter}
        viewport={viewport}
      />
      {showFilters && <PlacesFilters places={filteredPlaces as Place[]} />}
      <RenderMerchantOnboardVenues
        places={filteredPlaces}
        addMissingPlace={handleAddMissingVenue}
        getMore={handleGetMorePlaces}
      />
    </Container>
  );
};

const MerchantVenuesOnboarding: FunctionComponent<Props> = ({
  classes,
  match,
}) => {
  const [places, setPlaces] = useState<Place[] | []>([]);
  const [params, setParams] = useState<PlacesServiceParams>(
    {} as PlacesServiceParams,
  );
  const [filteredPlaces, setFilteredPlaces] = useState<Place[] | []>([]);
  const [stateFilter, setStateFilter] = useState<string>('');
  const [viewport, setViewport] = useState<GeoViewPort>(DEFAULT_GEO_VIEWPORT);
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const { merchantAcctDetails }: MerchantInfo = useGetMerchantInfo(match);
  const limit = VENUE_LIMIT;

  const handleOnCompleted = (data: any): void => {
    setPlaces(pathOr([], ['getPlaces', 'places'], data));
    setHasNextPage(pathOr(false, ['getPlaces', 'nextPage'], data));
  };

  const { placesFilters, state } = usePlacesFilters();
  const {
    loadPlacesServiceResults,
    placesSearchData,
    searchLoading,
  } = usePlacesSearch(GET_PLACES, {}, handleOnCompleted);

  useEffect(() => {
    if (isEmpty(merchantAcctDetails)) return;

    const {
      merchantUuid,
      companyName,
      latitude,
      longitude,
    } = merchantAcctDetails;
    const name = pathOr(companyName, ['params', 'merchantName'], match);

    let geocodeFilter = DEFAULT_GEO_CODE;
    let zoom = 3;

    if (latitude && longitude) {
      geocodeFilter = { latitude, longitude };
      zoom = 10;
    }

    setViewport({
      ...DEFAULT_GEO_VIEWPORT,
      ...geocodeFilter,
      zoom,
    });
    setParams({
      merchantUuid,
      name,
      limit,
      geocodeFilter,
    });
  }, [merchantAcctDetails]);

  useEffect(() => {
    setFilteredPlaces(places);
  }, [places]);

  useEffect(() => {
    const filteredResults = applyFiltersToPlaces(places, placesFilters);
    setFilteredPlaces(sortPlacesByState(filteredResults));
  }, [placesFilters]);

  useEffect(() => {
    setStateFilter(state);
  }, [state]);

  useEffect(() => {
    if (isEmpty(placesSearchData)) return;
    const { placesSearchText, placesSearchState } = placesSearchData;
    const { merchantUuid, latitude, longitude } = merchantAcctDetails;

    if (!searchLoading && placesSearchText !== '') {
      setStateFilter(placesSearchState);
      loadPlacesServiceResults({
        variables: {
          params: {
            merchantUuid,
            limit,
            name: placesSearchText,
            geocodeFilter: {
              latitude,
              longitude,
            },
            filters: {
              state: placesSearchState !== '' ? placesSearchState : null,
            },
          },
        },
      });
    }
  }, [placesSearchData]);

  const handleAddMissingVenue = (newPlace: Place) => {
    setPlaces([...places, newPlace]);
  };

  const handleGetMorePlaces = () => {
    if (hasNextPage) {
      const offset = page + 1;
      const { limit } = params as any;
      setPage(offset);
      loadPlacesServiceResults({
        variables: {
          params: {
            ...params,
            offset: limit * offset,
          },
        },
      });
    }
    return false;
  };

  if (isEmpty(params)) return null;

  const showFilters = places.length > MIN_NUMBER_OF_PLACES_TO_SHOW_FILTERS;

  return (
    <Grid className={classes.grid}>
      <Query
        enableProgressInfo
        query={GET_PLACES}
        onCompleted={handleOnCompleted}
        render={renderVenuesToOnboard(
          classes,
          filteredPlaces,
          stateFilter,
          searchLoading,
          showFilters,
          viewport,
          handleAddMissingVenue,
          handleGetMorePlaces,
        )}
        variables={{ params }}
      />
    </Grid>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(MerchantVenuesOnboarding);
